import type { z } from 'zod';
import { isAuthRetryableFetchError } from '@supabase/supabase-js';
import { ApiErrorSchema, type ApiError, type SyncConflictDetails } from '@shared/schemas';
import { supabase } from './supabaseClient';
import { resolveFixture, type FixtureKey } from './__fixtures__';

/**
 * QA Studio API client · Zod-parsed fetch wrapper over the Supabase edge
 * functions backend (ADR-012 / parity-ledger).
 *
 * Path convention: callers pass legacy `/api/<group>/<endpoint>` paths; the
 * client strips `/api` and routes to `<VITE_SUPABASE_URL>/functions/v1/<group>/...`.
 * Endpoint-wrapper files keep their Hono-era path strings so the flip is a
 * single point of control.
 *
 * Auth: bearer is read live from `supabase.auth.getSession()` on every call.
 * 401 → `supabase.auth.refreshSession()` single-flight retry once; if refresh
 * still fails the error propagates and the AuthContext SIGNED_OUT listener
 * resets the app to the LoginScreen.
 *
 * Dead-session bounce: a *terminal* refresh failure (invalid/expired refresh
 * token — anything that is NOT a retryable network blip) triggers a local
 * `signOut()`, so the `onAuthStateChange(SIGNED_OUT)` listener bounces a stale
 * authed shell to `/login` instead of leaving it firing perpetual 401s.
 *
 * Proactive keepalive: `refreshSessionIfExpiringSoon()` is called by the
 * session-keepalive DOM listeners (see `sessionKeepalive.ts`) on tab
 * wake/focus, so a token that lapsed while the tab slept is refreshed *before*
 * the debounced progress-sync flush fires a burst of would-be-401 PATCHes.
 *
 * Conflict (ADR-007): per-branch `If-Unmodified-Since` header is set from
 * `opts.branchUpdatedAt`; server returns 412 with `currentBranch` body so the
 * sync layer can refetch + retry (see `lib/storage-backend/progress-sync.ts`).
 *
 * MOCK_MODE (`VITE_API_MOCK=true`) short-circuits to typed fixtures so the
 * unit suite runs without a live edge runtime. Default is OFF — dev/prod use
 * real edge functions; tests opt in via `vi.stubEnv('VITE_API_MOCK', 'true')`.
 *
 * `apiFetchEnvelope` returns `{data, meta: {lastModified, etag}}` so the sync
 * layer can track per-branch `_updatedAt` for the next `If-Unmodified-Since`.
 * `apiFetch` is a thin shortcut returning just `.data`.
 */

function isMockMode(): boolean {
  return import.meta.env.VITE_API_MOCK === 'true';
}

function supabaseBaseUrl(): string {
  return ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(/\/$/, '');
}

function liveUrl(path: string): string {
  const stripped = path.replace(/^\/api/, '');
  return `${supabaseBaseUrl()}/functions/v1${stripped}`;
}

/** Refresh this far ahead of `expires_at` so a wake-up flush never races expiry. */
const PROACTIVE_REFRESH_THRESHOLD_MS = 120_000;

let _refreshPromise: Promise<boolean> | null = null;

async function getBearerToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Single-flight session refresh. Returns true on success. A *terminal* failure
 * (the refresh token is dead — i.e. NOT a retryable network error) clears the
 * local session via `signOut`, so the AuthContext SIGNED_OUT listener bounces
 * the app to `/login` rather than leaving a stale authed shell behind. A
 * retryable (network) failure returns false WITHOUT signing out, so a transient
 * blip doesn't evict a still-valid session.
 */
async function refreshSupabaseSession(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (!error) return true;
      if (!isAuthRetryableFetchError(error)) {
        // Local scope: just clear the dead session + emit SIGNED_OUT. No server
        // round-trip (it would fail anyway against a revoked refresh token).
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
      return false;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

/**
 * Proactively refresh when the access token is at/near expiry. Called by the
 * tab wake/focus keepalive so the token is fresh before the next request burst.
 * No-op when there is no session or expiry is comfortably in the future.
 */
export async function refreshSessionIfExpiringSoon(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session?.expires_at) return;
  const expiresAtMs = session.expires_at * 1000;
  if (expiresAtMs - Date.now() <= PROACTIVE_REFRESH_THRESHOLD_MS) {
    await refreshSupabaseSession();
  }
}

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export class SyncConflictError extends ApiClientError {
  readonly conflict: SyncConflictDetails;
  constructor(message: string, conflict: SyncConflictDetails) {
    super('syncConflict', message, 412, conflict);
    this.name = 'SyncConflictError';
    this.conflict = conflict;
  }
}

export interface ApiFetchOptions<TBody = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: TBody;
  /** Per-branch `If-Unmodified-Since` for ADR-007 LWW conflict detection. */
  branchUpdatedAt?: string;
  /** Skip access-token attach · used for purely public endpoints. */
  skipAuth?: boolean;
  /** Mock-mode override · normally inferred from path · used by tests. */
  fixtureKey?: FixtureKey;
  /** Mock-mode payload override · lets tests assert PATCH echo shape. */
  fixtureMutator?: (body: unknown) => unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

export interface ApiResponseMeta {
  /** Server `Last-Modified` header · server-truth branch timestamp for next If-Unmodified-Since. */
  lastModified: string | null;
  /** Server `ETag` header (quotes stripped) · sha256 of progress JSONB row. */
  etag: string | null;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: ApiResponseMeta;
}

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  const base = liveUrl(path);
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return {
      error: {
        code: 'badResponse',
        message: `Server responded ${res.status} with non-JSON body`,
      },
    };
  }
  const parsed = ApiErrorSchema.safeParse(body);
  if (parsed.success) return parsed.data;
  return {
    error: {
      code: 'unknownError',
      message: `Server responded ${res.status}`,
      details: body,
    },
  };
}

function shapeError(env: ApiError, status: number): ApiClientError {
  const { code, message, details } = env.error;
  if (code === 'syncConflict' && details) {
    return new SyncConflictError(message, details as SyncConflictDetails);
  }
  return new ApiClientError(code, message, status, details);
}

function stripEtag(raw: string | null): string | null {
  if (!raw) return null;
  return raw.replace(/^W\//, '').replace(/^"|"$/g, '');
}

async function rawFetch<T>(
  schema: z.ZodType<T>,
  path: string,
  opts: ApiFetchOptions,
  attempt: number,
): Promise<ApiEnvelope<T>> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (!opts.skipAuth) {
    const bearer = await getBearerToken();
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
  }
  if (opts.branchUpdatedAt) headers['If-Unmodified-Since'] = opts.branchUpdatedAt;

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      signal: opts.signal,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      // slot_sync_storm_fix · per-user API responses must never come from (or
      // populate) the browser HTTP cache — otherwise GET /progress cached for
      // one user is replayed for the next user on the same machine (cross-user
      // leak + stale board immune to localStorage clears). `no-store` also
      // bypasses any already-poisoned cache entry on the very next request.
      cache: 'no-store',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiClientError(
      'networkError',
      err instanceof Error ? err.message : 'Network request failed',
      0,
    );
  }

  if (res.status === 401 && !opts.skipAuth && attempt === 0) {
    const refreshed = await refreshSupabaseSession();
    if (refreshed) return rawFetch(schema, path, opts, attempt + 1);
    const env = await parseError(res);
    throw shapeError(env, 401);
  }

  const meta: ApiResponseMeta = {
    lastModified: res.headers.get('Last-Modified'),
    etag: stripEtag(res.headers.get('ETag')),
  };

  if (res.status === 204 || res.status === 304) {
    const empty = schema.safeParse(undefined);
    if (empty.success) return { data: empty.data, meta };
    return { data: undefined as T, meta };
  }

  if (!res.ok) {
    const env = await parseError(res);
    throw shapeError(env, res.status);
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiClientError('badResponse', 'Server returned malformed JSON', res.status);
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiClientError(
      'schemaMismatch',
      'Server response failed schema validation',
      res.status,
      parsed.error.format(),
    );
  }
  return { data: parsed.data, meta };
}

/**
 * Fire one API call · returns Zod-parsed payload envelope with response meta.
 * Sync-layer uses `meta.lastModified` to track per-branch `_updatedAt`;
 * UI-layer callers usually prefer `apiFetch` which discards meta.
 */
export async function apiFetchEnvelope<T>(
  schema: z.ZodType<T>,
  path: string,
  opts: ApiFetchOptions = {},
): Promise<ApiEnvelope<T>> {
  if (isMockMode()) {
    const data = resolveFixture({
      method: opts.method ?? 'GET',
      path,
      body: opts.body,
      mutator: opts.fixtureMutator,
      override: opts.fixtureKey,
    });
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiClientError(
        'fixtureMismatch',
        `Fixture for ${opts.method ?? 'GET'} ${path} failed schema validation`,
        0,
        parsed.error.format(),
      );
    }
    return { data: parsed.data, meta: { lastModified: null, etag: null } };
  }
  return rawFetch(schema, path, opts, 0);
}

/** Convenience: returns only `.data` from the envelope (most callers). */
export async function apiFetch<T>(
  schema: z.ZodType<T>,
  path: string,
  opts: ApiFetchOptions = {},
): Promise<T> {
  const { data } = await apiFetchEnvelope(schema, path, opts);
  return data;
}

/** Test-only · resets module-scope state between cases. */
export function __resetApiClientForTests(): void {
  _refreshPromise = null;
}

export const __isMockMode = isMockMode;
